import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Clock, User, ArrowLeft, CheckCircle, XCircle, AlertCircle, Navigation, MessageCircle, Phone } from "lucide-react";
import toast from "react-hot-toast";
import Chat from "./chat";
import tokenService from "../../../services/token-service";

const ScheduleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mapReady, setMapReady] = useState(false);
  const mapInstanceRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [isChatVisible, setIsChatVisible] = useState(false);

  // Job data state
  const [jobData, setJobData] = useState(null);

  useEffect(() => {
    const fetchJobDetails = async () => {
      setLoading(true);
      try {
        const token = tokenService.getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/services/customer/tasks/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          // Transform API data to component format if needed
          // Assuming API returns data structure similar to what we need
          // We might need to map fields if they differ
          setJobData(transformApiData(data.task || data.data));
        } else {
          throw new Error(data.message || "Failed to fetch job details");
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
        toast.error("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchJobDetails();
    }
  }, [id]);

  // Helper to transform API data to component state
  const transformApiData = (data) => {
    // If data is already in correct format, return it
    // Otherwise map it. Based on Schedule.jsx, we can infer structure.
    return {
      id: data.id || data._id,
      date: data.schedule?.date || data.date || "Date invalid",
      time: data.schedule?.time || data.time || "Time invalid",
      service: {
        title: data.service?.title || "Service",
        category: data.service?.category || "Category",
        subcategory: data.service?.subcategory || "Subcategory",
        description: data.service?.description || "",
        notes: data.service?.notes || "",
      },
      handyman: data.handyman ? {
        name: data.handyman.name,
        phone: data.handyman.phone,
        rating: data.handyman.rating || 0,
        completedJobs: data.handyman.completedJobs || 0,
        avatar: data.handyman.avatar,
      } : null,
      location: {
        address: data.location?.address || "",
        coordinates: data.location?.coordinates || [31.4782, 74.3140],
        fullAddress: data.location?.fullAddress || "",
      },
      workDetails: {
        price: data.pricing?.finalPrice || data.pricing?.basePrice || 0,
        estimatedTime: data.schedule?.estimatedDuration || "N/A",
        status: data.status || "pending",
        scheduledDate: data.schedule?.date,
        scheduledTime: data.schedule?.time,
      },
    };
  };

  // Map setup (similar to handyman schedule detail)
  useEffect(() => {
    if (scriptLoadedRef.current || document.querySelector('script[src*="leaflet"]')) {
      initMap();
      return;
    }

    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(link);
    }

    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.async = true;

    script.onload = () => {
      scriptLoadedRef.current = true;
      initMap();
    };

    script.onerror = () => {
      console.error("Failed to load Leaflet library");
      setMapReady(true);
    };

    document.body.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const initMap = () => {
    if (!jobData?.location?.coordinates) return;

    try {
      const mapElement = document.getElementById("schedule-map");
      if (!mapElement || !window.L) return;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      const [lat, lng] = jobData.location.coordinates;
      const map = window.L.map("schedule-map").setView([lat, lng], 15);
      mapInstanceRef.current = map;

      window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(map);

      const marker = window.L.marker([lat, lng])
        .addTo(map)
        .bindPopup(`
          <div class="p-2">
            <p class="font-semibold text-gray-900">Service Location</p>
            <p class="text-sm text-gray-600">${jobData.location.address}</p>
          </div>
        `)
        .openPopup();

      const customIcon = window.L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: #EFA938; width: 30px; height: 30px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
      });

      marker.setIcon(customIcon);
      setMapReady(true);
    } catch (error) {
      console.error("Map initialization error:", error);
      setMapReady(true);
    }
  };

  useEffect(() => {
    if (jobData?.location?.coordinates && window.L && !loading) {
      initMap();
    }
  }, [jobData, loading]);

  const handleOpenDirections = () => {
    const [lat, lng] = jobData.location.coordinates;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
          <p className="text-gray-600 text-sm font-medium">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-gray-500">Task details not found</div>
      </div>
    );
  }

  const statusConfig = {
    confirmed: {
      label: "Confirmed",
      color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800",
      icon: CheckCircle,
    },
    pending: {
      label: "Pending",
      color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800",
      icon: AlertCircle,
    },
    cancelled: {
      label: "Cancelled",
      color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800",
      icon: XCircle,
    },
  };

  const currentStatus = statusConfig[jobData.workDetails?.status] || statusConfig.pending;
  const StatusIcon = currentStatus.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-4 shadow-sm rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/customer/schedule")}
              className="p-2 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Schedule Details</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{jobData.date} · {jobData.time}</p>
            </div>
          </div>
          <div className="ml-auto">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${currentStatus.color}`}>
              <StatusIcon className="h-4 w-4" />
              <span className="text-sm font-semibold">{currentStatus.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
        <div className="h-[400px] relative bg-gray-100 dark:bg-slate-900" id="schedule-map">
          {!mapReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm z-50">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
                <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Loading map...</p>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-[#EFA938]" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{jobData.location.address}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{jobData.location.fullAddress}</p>
              </div>
            </div>
            <button
              onClick={handleOpenDirections}
              className="px-4 py-2 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors flex items-center gap-2"
            >
              <Navigation className="h-4 w-4" />
              Directions
            </button>
          </div>
        </div>
      </div>

      {/* Handyman & Service Details */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Handyman Details Card */}
        <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <User className="h-5 w-5 text-[#EFA938]" />
              Handyman Details
            </h2>
          </div>
          <div className="px-6 py-6 space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#EFA938] to-orange-400 flex items-center justify-center text-white text-2xl font-bold shadow-lg ring-4 ring-amber-100 dark:ring-amber-900">
                  {jobData?.handyman?.name.charAt(0)}
                </div>
                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-green-500 border-2 border-white dark:border-slate-800"></div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{jobData?.handyman?.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-lg">
                        {i < Math.floor(jobData?.handyman?.rating) ? "★" : "☆"}
                      </span>
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">({jobData?.handyman?.rating})</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-slate-700 space-y-3">
              {!isChatVisible ? (
                <button
                  onClick={() => setIsChatVisible(true)}
                  className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl bg-gradient-to-r from-[#EFA938] to-orange-400 text-white font-semibold hover:from-[#d78c1f] hover:to-orange-500 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span>Start Chat</span>
                </button>
              ) : (
                <div className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-sm font-semibold text-green-700 dark:text-green-400">Chat Active</span>
                </div>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                <span className="font-medium">Privacy Protected:</span> Use the chat feature to communicate securely. Handyman contact information is kept private.
              </p>
            </div>
          </div>
        </div>

        {/* Service Details Card */}
        <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#EFA938]" />
              Service Details
            </h2>
          </div>
          <div className="px-6 py-6 space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{jobData.service.title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{jobData.service.category} · {jobData.service.subcategory}</p>
            </div>
            <div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{jobData.service.description}</p>
            </div>
            {jobData.service.notes && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-400">Notes:</p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{jobData.service.notes}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-slate-700">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Price</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">PKR {jobData.workDetails.price.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Duration</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">{jobData.workDetails.estimatedTime}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Component */}
      {isChatVisible && jobData.handyman && (
        <Chat
          handymanName={jobData.handyman.name}
          handymanAvatar={jobData.handyman.avatar}
          isLive={true}
          onEndChat={() => {
            setIsChatVisible(false);
          }}
        />
      )}
    </div>
  );
};

export default ScheduleDetail;

